import { useState } from "react";
import { ArrowDown, ExternalLink, Info, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useSubscription } from "@/hooks/useSubscription";
import { useWorkspace } from "@/hooks/useWorkspace";
import { downgradeTargets, tierDisplayName, type Tier } from "@/lib/tier";
import { openBillingPortal } from "@/lib/billing";
import { NewWorkspaceDialog } from "@/components/NewWorkspaceDialog";
import { DowngradeDialog } from "@/components/DowngradeDialog";

export const SubscriptionCard = ({ accountTier }: { accountTier: Tier | null }) => {
  const { workspaces, workspaceLimit } = useWorkspace();
  const { subscription, isActive, isTrialing, trialDaysRemaining, trialEndsAt } = useSubscription();
  const [portalLoading, setPortalLoading] = useState(false);
  const [newWsOpen, setNewWsOpen] = useState(false);
  const [downgradeOpen, setDowngradeOpen] = useState(false);

  const openPortal = async () => {
    setPortalLoading(true);
    await openBillingPortal();
    setPortalLoading(false);
  };

  return (
    <>
      <Card className="shadow-soft">
        <CardHeader><CardTitle>Subscription</CardTitle></CardHeader>
        <CardContent className="space-y-3 text-sm">
          {isActive && subscription ? (
            <>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Plan</span>
                {accountTier === "comp" || accountTier === "admin_workspace" ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge className="gap-1 cursor-help">
                        {accountTier === "comp" ? "Comp (Admin grant)" : "Admin workspace"}
                        <Info className="h-3 w-3" />
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      {accountTier === "comp"
                        ? "Complimentary plan granted by an admin. Overrides any active subscription."
                        : "Internal admin workspace. Not billed."}
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <Badge>{tierDisplayName(accountTier)}</Badge>
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Workspaces</span>
                <span className="font-medium">
                  {workspaces.length}
                  {workspaceLimit !== null && ` / ${workspaceLimit}`}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Status</span>
                <span className="font-medium">
                  {subscription.cancel_at_period_end ? "Cancels at period end" : subscription.status}
                </span>
              </div>
              {subscription.current_period_end && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">
                    {isTrialing
                      ? "Trial ends"
                      : subscription.cancel_at_period_end
                        ? "Access ends"
                        : "Renews"}
                  </span>
                  <span className="font-medium">
                    {new Date(subscription.current_period_end).toLocaleDateString()}
                    {isTrialing && trialDaysRemaining !== null && (
                      <span className="ml-1 text-muted-foreground">
                        ({trialDaysRemaining === 0 ? "today" : `${trialDaysRemaining}d left`})
                      </span>
                    )}
                  </span>
                </div>
              )}
              {isTrialing && (
                <p className="text-xs text-muted-foreground">
                  Your card will be charged on{" "}
                  {trialEndsAt ? trialEndsAt.toLocaleDateString() : "trial end"}.
                  Cancel any time before then via the billing portal to avoid the charge.
                </p>
              )}
              {(accountTier === "comp" || accountTier === "admin_workspace") && (
                <p className="text-xs text-muted-foreground">
                  Your workspace is on a complimentary plan. This paid subscription will end{" "}
                  {subscription.current_period_end
                    ? `on ${new Date(subscription.current_period_end).toLocaleDateString()}`
                    : "at the end of the current billing period"}{" "}
                  and won't renew.
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button onClick={openPortal} disabled={portalLoading} variant="outline">
                  <ExternalLink className="h-4 w-4" />
                  {portalLoading ? "Opening…" : "Manage billing"}
                </Button>
                {downgradeTargets(accountTier).length > 0 && (
                  <Button variant="outline" onClick={() => setDowngradeOpen(true)}>
                    <ArrowDown className="h-4 w-4" />
                    Change plan
                  </Button>
                )}
                <Button variant="outline" onClick={() => setNewWsOpen(true)}>
                  <Plus className="h-4 w-4" />
                  New workspace
                </Button>
              </div>
              {subscription.cancel_at_period_end && (
                <p className="text-xs text-muted-foreground">
                  Changed your mind? You can resume anytime
                  {subscription.current_period_end
                    ? ` before ${new Date(subscription.current_period_end).toLocaleDateString()}`
                    : ""}
                  {" "}from the billing portal.
                </p>
              )}
            </>
          ) : accountTier === "comp" || accountTier === "admin_workspace" ? (
            <p className="text-muted-foreground">
              You're on a complimentary {accountTier === "admin_workspace" ? "admin" : "comp"} plan — no billing required.
            </p>
          ) : (
            <p className="text-muted-foreground">
              You don't have an active subscription. <a href="/pricing" className="text-primary underline">View plans</a>.
            </p>
          )}
        </CardContent>
      </Card>

      <NewWorkspaceDialog open={newWsOpen} onOpenChange={setNewWsOpen} />
      <DowngradeDialog open={downgradeOpen} onOpenChange={setDowngradeOpen} />
    </>
  );
};
